/**
 * Human-readable label for an order's status.
 * Business Rule #17: pickup orders relabel `out_for_delivery` and `delivered` to "Collected".
 * @param {{ order_status?: string, delivery_type?: string }} order
 * @returns {string}
 */
export function getOrderStatusLabel(order) {
  const status = order?.order_status || '';
  const isPickup = (order?.delivery_type || 'pickup') === 'pickup';
  if (isPickup && (status === 'out_for_delivery' || status === 'delivered')) {
    return 'Collected';
  }
  if (status === 'cancelled') return 'Cancelled';
  return status.replace(/_/g, ' ');
}

/** Hours window for post-completion ratings — must match backend RATING_WINDOW_HOURS. */
export const RATING_WINDOW_HOURS = 48;

/**
 * Is this order rateable right now? Mirrors backend `_rating_eligibility` (Business Rule #19).
 * Eligible when delivery order is `delivered`, OR pickup order has `pickup_scanned_at` set,
 * AND we're within 48h of completion, AND no rating has been submitted yet.
 * @param {object} order
 * @returns {boolean}
 */
export function isRateable(order) {
  if (!order || order.rating) return false;
  const isPickup = order.delivery_type === 'pickup';
  const completion = isPickup
    ? order.pickup_scanned_at
    : (order.order_status === 'delivered' ? (order.updated_at || order.ready_at) : null);
  if (!completion) return false;
  const ageMs = Date.now() - new Date(completion).getTime();
  if (Number.isNaN(ageMs) || ageMs < 0) return false;
  return ageMs <= RATING_WINDOW_HOURS * 60 * 60 * 1000;
}

/** Hours window for raising a complaint — must match backend COMPLAINT_WINDOW_HOURS (Business Rule #20). */
export const COMPLAINT_WINDOW_HOURS = 48;

/**
 * Pickup-QR rollout cutoff: only orders created strictly after this instant are eligible
 * to show the pickup QR. `2026-05-17T23:59:59 IST` == `2026-05-17T18:29:59Z` UTC.
 * Must match backend PICKUP_QR_CUTOFF_ISO in routers/orders.py.
 */
export const PICKUP_QR_CUTOFF_ISO = '2026-05-17T18:29:59Z';

/**
 * Max age in hours after which a pickup QR stops appearing — measured from `created_at`.
 * Prevents stale uncollected orders from popping up forever on the home-screen popup and
 * inline order pages. Must match backend PICKUP_QR_MAX_AGE_HOURS in routers/orders.py.
 */
export const PICKUP_QR_MAX_AGE_HOURS = 24;

/**
 * Should the pickup QR card render for this order? Combines all customer-side gates:
 * pickup delivery, not cancelled, status == "ready", not yet collected, created after
 * the rollout cutoff, and within PICKUP_QR_MAX_AGE_HOURS of `created_at` (Business Rule #17).
 * Backend `get_pickup_qr` enforces the same gates.
 * @param {object} order
 * @returns {boolean}
 */
export function isPickupQrEligible(order) {
  if (!order) return false;
  if (order.delivery_type !== 'pickup') return false;
  if (order.order_status !== 'ready') return false;
  if (order.order_status === 'cancelled') return false;
  if (order.pickup_scanned_at) return false;
  const createdMs = new Date(order.created_at).getTime();
  if (Number.isNaN(createdMs)) return false;
  if (createdMs <= new Date(PICKUP_QR_CUTOFF_ISO).getTime()) return false;
  const ageMs = Date.now() - createdMs;
  return ageMs <= PICKUP_QR_MAX_AGE_HOURS * 60 * 60 * 1000;
}

/**
 * localStorage key prefix for the home-screen ReviewPrompt dismissal flag (Business Rule #19).
 * Stored as `${REVIEW_PROMPT_DISMISS_KEY_PREFIX}${order.id}` → "1". Permanent once set —
 * the customer can still rate via the Profile / OrderTracking pills.
 */
export const REVIEW_PROMPT_DISMISS_KEY_PREFIX = 'pbc_review_prompt_dismissed::';

/**
 * Pick the most recent rateable order that hasn't been dismissed from the home-screen prompt.
 * Returns null when no order qualifies. Sorts by completion-time desc so newer orders win
 * even if the array isn't pre-sorted.
 * @param {Array<object>} orders
 * @returns {object|null}
 */
export function findPromptableOrder(orders) {
  if (!Array.isArray(orders) || orders.length === 0) return null;
  const candidates = orders
    .filter((o) => o && isRateable(o))
    .filter((o) => {
      try {
        return localStorage.getItem(`${REVIEW_PROMPT_DISMISS_KEY_PREFIX}${o.id}`) !== '1';
      } catch {
        return true;
      }
    });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const aT = new Date(a.pickup_scanned_at || a.updated_at || a.ready_at || a.created_at || 0).getTime();
    const bT = new Date(b.pickup_scanned_at || b.updated_at || b.ready_at || b.created_at || 0).getTime();
    return bT - aT;
  });
  return candidates[0];
}

/**
 * Can the customer file a complaint on this order right now? Mirrors backend
 * `_is_order_complainable` (Business Rule #20). Same completion gate as `isRateable`
 * but doesn't require "no rating yet" — ratings and complaints are independent.
 * @param {object} order
 * @returns {boolean}
 */
export function isComplainable(order) {
  if (!order) return false;
  const isPickup = order.delivery_type === 'pickup';
  const completion = isPickup
    ? order.pickup_scanned_at
    : (order.order_status === 'delivered' ? (order.updated_at || order.ready_at) : null);
  if (!completion) return false;
  const ageMs = Date.now() - new Date(completion).getTime();
  if (Number.isNaN(ageMs) || ageMs < 0) return false;
  return ageMs <= COMPLAINT_WINDOW_HOURS * 60 * 60 * 1000;
}

